// DialogEntityDocuments — Reusable documents dialog for any entity detail page

// Shows all documents linked to an entity (contract, invoice, etc.) via
// source_type + source_id OR structured tags. Supports upload, view, delete.
// Mirrors the DialogAuditTimeline pattern for consistent UX.

import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	alpha,
	Avatar,
	Box,
	Button,
	Chip,
	Dialog,
	DialogContent,
	IconButton,
	LinearProgress,
	Stack,
	Tooltip,
	Typography,
	useTheme,
} from '@mui/material';
import {
	Close as IconClose,
	CloudUpload as IconUpload,
	Description as IconDocument,
	InsertDriveFile as IconFile,
	Visibility as IconView,
} from '@mui/icons-material';

import { ContextUser } from '../../../App.context';
import { fetchAiDocuments, uploadAiDocument } from '../../../services/aiApi';
import type { AiDocument } from '../../pages/ProjectV2/Documents/documentTypes';
import { formatDate } from '../../../utils/dates';
import { radius } from '../../../utils/designSystem';
import { Empty } from '../../ui/Empty/Empty';

// Types

export interface DialogEntityDocumentsProps {
	isOpen: boolean;
	onClose: () => void;
	/** Display title, e.g. "Contract Documents" */
	title?: string;
	/** Primary source_type for fetching & uploading */
	sourceType: string;
	/** Primary source_id for fetching & uploading */
	sourceId: number;
	/** Project ID for document isolation */
	projectId?: number;
	/** Called when user clicks "View" on a document */
	onViewDocument?: (doc: AiDocument) => void;
	/** When true, hides upload actions */
	readOnly?: boolean;
	/**
	 * Optional lookup tables for condition / section / field names. When
	 * provided, the dialog renders condition-tagged documents grouped under
	 * their condition (and labelled with the requirement field they were
	 * uploaded against) instead of as a flat list.
	 */
	conditionNamesById?: Record<number, string>;
	fieldLabelsById?: Record<string, string>;
}

// Helpers

const formatFileSize = (bytes: number | string | undefined): string => {
	const n = Number(bytes);
	if (!n || n <= 0) return '';
	const units = ['B', 'KB', 'MB', 'GB'];
	const exp = Math.floor(Math.log(n) / Math.log(1024));
	return `${parseFloat((n / Math.pow(1024, exp)).toFixed(1))} ${units[exp]}`;
};

const getUploaderName = (doc: AiDocument): string => {
	if (typeof doc.uploaded_by === 'object' && doc.uploaded_by) {
		return doc.uploaded_by.displayName || doc.uploaded_by.email || '';
	}
	return '';
};

// Component

export const DialogEntityDocuments = ({
	isOpen,
	onClose,
	title = 'Documents',
	sourceType,
	sourceId,
	projectId,
	onViewDocument,
	readOnly = false,
	conditionNamesById,
	fieldLabelsById,
}: DialogEntityDocumentsProps) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const { user } = useContext(ContextUser);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const [documents, setDocuments] = useState<AiDocument[]>([]);
	const [loading, setLoading] = useState(false);
	const [uploading, setUploading] = useState(false);
	const [uploadProgress, setUploadProgress] = useState(0);

	// Fetch documents for this entity (by structured tag)
	const fetchDocs = useCallback(async () => {
		if (!sourceId) return;
		setLoading(true);
		try {
			const response = await fetchAiDocuments({
				tags: `${sourceType}:${sourceId}`,
				project_id: projectId,
				populate: 'uploaded_by',
				page_size: 100,
				order_by: 'uploaded_at',
				order: 'DESC',
			});
			setDocuments(response.data || []);
		} catch (err) {
			console.error('Failed to fetch entity documents:', err);
		} finally {
			setLoading(false);
		}
	}, [sourceType, sourceId, projectId]);

	// Load on open
	useEffect(() => {
		if (isOpen) fetchDocs();
	}, [isOpen, fetchDocs]);

	// Upload handler
	const handleFileChange = useCallback(
		async (e: React.ChangeEvent<HTMLInputElement>) => {
			const files = Array.from(e.target.files || []);
			if (files.length === 0 || !user?.id) return;

			setUploading(true);
			setUploadProgress(0);
			try {
				for (let i = 0; i < files.length; i++) {
					await uploadAiDocument({
						file: files[i],
						source_type: sourceType,
						source_id: sourceId,
						project_id: projectId,
						tag: `${sourceType}:${sourceId}`,
					});
					setUploadProgress(Math.round(((i + 1) / files.length) * 100));
				}
				await fetchDocs();
			} catch (err) {
				console.error('Upload failed:', err);
			} finally {
				setUploading(false);
				setUploadProgress(0);
				// Reset input so the same file can be re-selected
				if (fileInputRef.current) fileInputRef.current.value = '';
			}
		},
		[sourceType, sourceId, projectId, user, fetchDocs]
	);

	return (
		<Dialog
			open={isOpen}
			onClose={onClose}
			fullWidth
			maxWidth="md"
			PaperProps={{ sx: { borderRadius: radius.lg, maxHeight: '80vh' } }}>
			{/* Header */}
			<Box
				sx={{
					px: 3,
					py: 2,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					borderBottom: `1px solid ${theme.palette.divider}`,
				}}>
				<Stack direction="row" spacing={1.5} alignItems="center">
					<Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), width: 36, height: 36 }}>
						<IconDocument sx={{ color: 'primary.main', fontSize: 20 }} />
					</Avatar>
					<Typography variant="h6" fontWeight={700}>
						{title}
					</Typography>
					<Chip label={documents.length} size="small" sx={{ fontWeight: 600 }} />
				</Stack>
				<Stack direction="row" spacing={1} alignItems="center">
					{!readOnly && (
						<>
							<input ref={fileInputRef} type="file" multiple hidden onChange={handleFileChange} />
							<Button
								variant="contained"
								size="small"
								startIcon={<IconUpload />}
								disabled={uploading}
								onClick={() => fileInputRef.current?.click()}
								sx={{ borderRadius: radius.md, textTransform: 'none', fontWeight: 600 }}>
								Upload
							</Button>
						</>
					)}
					<IconButton onClick={onClose} size="small">
						<IconClose />
					</IconButton>
				</Stack>
			</Box>

			{/* Upload progress */}
			{uploading && (
				<LinearProgress variant="determinate" value={uploadProgress} sx={{ height: 3 }} />
			)}

			{/* Content */}
			<DialogContent sx={{ p: 0 }}>
				{loading ? (
					<Box sx={{ py: 4 }}>
						<Empty
							isLoading
							IconCircleProps={{ type: 'document', size: 'md' }}
							title={{ children: '' }}
							content={{ children: t('loadingDocuments') }}
						/>
					</Box>
				) : documents.length === 0 ? (
					<Box sx={{ py: 4 }}>
						<Empty
							IconCircleProps={{ type: 'document', size: 'md' }}
							title={{ children: t('noDocumentsYet') }}
							content={{ children: t('uploadToGetStarted') }}
							actions={
								readOnly
									? []
									: [
											{
												children: t('uploadDocument'),
												variant: 'contained',
												startIcon: <IconUpload />,
												onClick: () => fileInputRef.current?.click(),
											},
										]
							}
						/>
					</Box>
				) : (
					<Box>
						{(() => {
							// Parse tags into condition/field metadata, then bucket.
							type Bucket = { key: string; label: string; docs: AiDocument[] };
							const docConditionId = (doc: AiDocument): number | null => {
								const tags = (doc.metadata as Record<string, unknown> | undefined)?.tags;
								if (!Array.isArray(tags)) return null;
								for (const t of tags as string[]) {
									if (typeof t === 'string' && t.startsWith('condition:')) {
										const n = Number(t.slice('condition:'.length));
										if (Number.isFinite(n)) return n;
									}
								}
								return null;
							};
							const docFieldId = (doc: AiDocument): string | null => {
								const tags = (doc.metadata as Record<string, unknown> | undefined)?.tags;
								if (!Array.isArray(tags)) return null;
								for (const t of tags as string[]) {
									if (typeof t === 'string' && t.startsWith('requirement_field:')) {
										return t.slice('requirement_field:'.length);
									}
								}
								return null;
							};
							const hasConditionTags = documents.some((d) => docConditionId(d) !== null);
							const buckets: Bucket[] = [];
							if (hasConditionTags) {
								const byCond = new Map<string, AiDocument[]>();
								const unscoped: AiDocument[] = [];
								documents.forEach((d) => {
									const cid = docConditionId(d);
									if (cid == null) {
										unscoped.push(d);
										return;
									}
									const k = String(cid);
									if (!byCond.has(k)) byCond.set(k, []);
									byCond.get(k)?.push(d);
								});
								Array.from(byCond.entries())
									.sort(([a], [b]) => Number(a) - Number(b))
									.forEach(([cid, docs]) => {
										const name = conditionNamesById?.[Number(cid)];
										buckets.push({
											key: `cond-${cid}`,
											label: name ? `${name} (milestone #${cid})` : `Milestone #${cid}`,
											docs,
										});
									});
								if (unscoped.length > 0) {
									buckets.push({
										key: 'unscoped',
										label: t('contractLevelDocuments'),
										docs: unscoped,
									});
								}
							} else {
								buckets.push({ key: 'all', label: '', docs: documents });
							}

							let rowIdx = 0;
							return buckets.map((bucket) => (
								<Box key={bucket.key}>
									{bucket.label && (
										<Box
											sx={{
												px: 2.5,
												py: 1,
												bgcolor: alpha(theme.palette.primary.main, 0.04),
												borderTop: `1px solid ${theme.palette.divider}`,
											}}>
											<Typography
												sx={{
													fontSize: '1.1rem',
													fontWeight: 700,
													color: 'primary.main',
													textTransform: 'uppercase',
													letterSpacing: '0.08em',
												}}>
												{bucket.label} · {bucket.docs.length}
											</Typography>
										</Box>
									)}
									{bucket.docs.map((doc) => {
										const idx = rowIdx++;
										const metadata = (doc.metadata || {}) as Record<string, unknown>;
										const sizeStr = formatFileSize(
											metadata.file_size_bytes as string | number | undefined
										);
										const uploaderName = getUploaderName(doc);
										const fieldId = docFieldId(doc);
										const fieldLabel =
											fieldId && fieldLabelsById?.[fieldId] ? fieldLabelsById[fieldId] : fieldId;
										return (
											<Stack
												key={doc.id || idx}
												direction="row"
												alignItems="center"
												spacing={2}
												sx={{
													px: 2.5,
													py: 1.5,
													bgcolor: idx % 2 === 0 ? 'transparent' : 'action.hover',
													'&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
												}}>
												<Avatar
													sx={{
														bgcolor: alpha(theme.palette.primary.main, 0.08),
														width: 36,
														height: 36,
														flexShrink: 0,
													}}>
													<IconFile sx={{ color: 'primary.main', fontSize: 18 }} />
												</Avatar>
												<Box sx={{ flex: 1, minWidth: 0 }}>
													<Typography
														sx={{ fontWeight: 600, fontSize: '1.3rem' }}
														noWrap
														title={doc.filename}>
														{doc.filename}
													</Typography>
													<Stack direction="row" spacing={1} alignItems="center">
														{fieldLabel && (
															<Chip
																label={fieldLabel}
																size="small"
																sx={{
																	height: 20,
																	fontSize: '1.1rem',
																	bgcolor: alpha(theme.palette.info.main, 0.1),
																	color: 'info.main',
																}}
															/>
														)}
														{doc.doc_type && (
															<Chip
																label={doc.doc_type}
																size="small"
																sx={{ height: 20, fontSize: '1.1rem' }}
															/>
														)}
														{sizeStr && (
															<Typography
																variant="caption"
																color="text.secondary"
																sx={{ fontSize: '1.1rem' }}>
																{sizeStr}
															</Typography>
														)}
														{doc.uploaded_at && (
															<Typography
																variant="caption"
																color="text.secondary"
																sx={{ fontSize: '1.1rem' }}>
																{formatDate({ date: doc.uploaded_at })}
															</Typography>
														)}
														{uploaderName && (
															<Typography
																variant="caption"
																color="text.secondary"
																sx={{ fontSize: '1.1rem' }}>
																{t('uploadedBy', { name: uploaderName })}
															</Typography>
														)}
													</Stack>
												</Box>
												<Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
													{onViewDocument && (
														<Tooltip title={t('viewDocument')}>
															<IconButton size="small" onClick={() => onViewDocument(doc)}>
																<IconView fontSize="small" />
															</IconButton>
														</Tooltip>
													)}
												</Stack>
											</Stack>
										);
									})}
								</Box>
							));
						})()}
					</Box>
				)}
			</DialogContent>
		</Dialog>
	);
};
